import { action, computed, observable } from 'mobx';
import {
    IFlowGalleryTemplate,
    TRepoKey
} from '~/components/FlowGallery/types/flow-gallery.types';
import {
    AtoZcomparator,
    comparator,
    numberComparator
} from '~/utils/automation.utils';
import type { RootStore } from '~/stores/root.store';
import {
    TGalleryDashboardSortBy,
    TTagsProperty,
    UserTemplate
} from '~/components/GalleryDashboard/gallery-dashboard.types';
import { ALL_TAGS_VALUES } from '~/components/GalleryDashboard/GalleryDashboard.constants';

type TLoaderId =
    | 'deleteTemplate'
    | 'publishTemplate'
    | 'saveTemplate'
    | 'loadTemplates';

const INITIAL_LOADING_STATUSES: Record<TLoaderId, boolean> = {
    deleteTemplate: false,
    publishTemplate: false,
    saveTemplate: false,
    loadTemplates: false
};

export class GalleryDashboardUiStore {
    @observable loadingStatuses: Record<TLoaderId, boolean> =
        INITIAL_LOADING_STATUSES;
    @observable isModalOpen = false;
    @observable currentRepository: TRepoKey;
    @observable sortBy: TGalleryDashboardSortBy;
    @observable allTemplatesCount: number;
    @observable selectedTemplate: IFlowGalleryTemplate;
    @observable private _templates: IFlowGalleryTemplate[];
    @observable repoTags: string[];
    @observable repoSystemTags: string[];
    @observable isCreateNewTemplate: boolean;
    @observable isModalPublishButtonClicked = false;
    @observable isDialogPublishButtonClicked = false;
    @observable isUserTemplateSelectionModalOpen = false;
    @observable searchedUserMail: string;
    @observable userTemplates: UserTemplate[];
    @observable isImportModalOpen = false;
    @observable importedSelectedTemplates: UserTemplate[] = [];
    @observable isInvalidUser: boolean = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.resetGalleryDashboard();
        this.rootStore = rootStore;
    }

    @computed
    get templates() {
        return this._templates?.slice().sort((a, b) => {
            switch (this.sortBy) {
                case 'name-asc':
                    return AtoZcomparator(b, a);
                case 'name-desc':
                    return AtoZcomparator(a, b);
                case 'imports-asc':
                    return numberComparator(a, b);
                case 'imports-desc':
                    return numberComparator(b, a);
                case 'date-desc':
                    return comparator(b.updated_at, a.updated_at);
                case 'date-asc':
                    return comparator(a.updated_at, b.updated_at);
                case 'desc-asc':
                    return comparator(b.description || '', a.description || '');
                case 'desc-desc':
                    return comparator(a.description || '', b.description || '');
                default:
                    return 0;
            }
        });
    }

    @computed
    get isModalFormValid() {
        if (this.currentRepository === 'prepublish_templates') {
            return (
                !!this.selectedTemplate?.title &&
                !!this.selectedTemplate?.template_id
            );
        } else {
            return !!this.selectedTemplate?.title;
        }
    }

    @action.bound
    setLoadingStatuses(loaderId: TLoaderId, isLoading: boolean) {
        this.loadingStatuses[loaderId] = isLoading;
    }

    @action.bound
    loadTemplates(templates: IFlowGalleryTemplate[] = []) {
        this._templates = templates;

        if (this.allTemplatesCount < templates.length) {
            this.allTemplatesCount = templates.length;
        }
    }

    @action.bound
    setAllRepoTags(tags: string[], sysTags: string[]) {
        this.repoTags = tags;
        this.repoSystemTags = sysTags;
    }

    @action.bound
    setIsInvalidUser(isInvalid: boolean) {
        this.isInvalidUser = isInvalid;
    }

    @action.bound
    mergeResponse(template: IFlowGalleryTemplate) {
        this._templates.splice(
            this._templates.findIndex(
                _template => _template._id === template._id
            ),
            1,
            template
        );
    }

    @action.bound
    deleteCurrentTemplate() {
        this._templates = this._templates.filter(
            template => template._id !== this.selectedTemplate._id
        );
    }

    @action.bound
    addTemplate(template: IFlowGalleryTemplate) {
        this._templates.push(template);
    }
    @action.bound
    setSelectedImportedTemplates(templates: UserTemplate[]) {
        this.importedSelectedTemplates = templates;
    }

    @action.bound
    clearTemplateToImports() {
        this.importedSelectedTemplates = [];
    }

    @action.bound
    editSelectedTemplate(template: Partial<IFlowGalleryTemplate>) {
        this.selectedTemplate = { ...this.selectedTemplate, ...template };
    }

    @action.bound
    setSortBy(sortBy: TGalleryDashboardSortBy) {
        this.sortBy = sortBy;
    }

    @action.bound
    setCurrentRepository(repository: TRepoKey) {
        this.currentRepository = repository;
    }

    @action.bound
    setIsCreateNewTemplate(isCreate: boolean) {
        this.isCreateNewTemplate = isCreate;
    }

    @action.bound
    onCreateNewTemplateClick() {
        this.isCreateNewTemplate = true;
        this.isModalOpen = true;
    }

    @action.bound
    onImportTemplatesClick() {
        this.isCreateNewTemplate = true;
        this.isImportModalOpen = true;
    }

    @action.bound
    setIsImportModalOpen(isImportModalOpen: boolean) {
        this.isImportModalOpen = isImportModalOpen;
    }

    @action.bound
    setIsTemplateSelectionModalOpen(isOpen: boolean) {
        this.isUserTemplateSelectionModalOpen = isOpen;
    }

    @action.bound
    editSearchedUserMail(userEmail: string) {
        this.searchedUserMail = userEmail;
    }

    @action.bound
    setUserTemplates(templates) {
        this.userTemplates = templates;
    }

    @action.bound
    toggleFilter(filter: { isActive?: boolean }) {
        filter.isActive = !filter.isActive;
    }

    @action.bound
    openModal() {
        this.isModalOpen = true;
    }

    @action.bound
    closeModal() {
        this.isModalOpen = false;
        this.selectedTemplate = null;
        this.isCreateNewTemplate = null;
        this.isDialogPublishButtonClicked = false;
        this.isModalPublishButtonClicked = false;
    }

    @action.bound
    resetGalleryDashboard() {
        this.setLoadingStatuses('loadTemplates', true);
        this.currentRepository = 'flow_gallery';
        this.sortBy = 'imports-desc';
        this.allTemplatesCount = null;
        this.selectedTemplate = null;
        this._templates = null;
    }

    @action.bound
    setIsModalPublishButtonClicked(isClicked: boolean) {
        this.isModalPublishButtonClicked = isClicked;
    }

    @action.bound
    setIsDialogPublishButtonClicked(isClicked: boolean) {
        this.isDialogPublishButtonClicked = isClicked;
    }

    @action.bound
    closeModalAndResetState() {
        this.editSelectedTemplate(null);
        this.setIsDialogPublishButtonClicked(false);
        this.closeModal();
    }

    @action.bound
    toggleTagSelection(selectedTag, tagsProperty: TTagsProperty) {
        const isTagSelected = this.selectedTemplate?.[tagsProperty]?.some(
            tag => tag === selectedTag
        );

        if (Object.values(ALL_TAGS_VALUES).includes(selectedTag)) {
            this.toggleAllTagsSelection(tagsProperty);
            return;
        }

        if (isTagSelected) {
            this.editSelectedTemplate({
                [tagsProperty]: this.selectedTemplate?.[tagsProperty]?.filter(
                    tag => tag !== selectedTag
                )
            });
            return;
        }

        this.editSelectedTemplate({
            [tagsProperty]: this.selectedTemplate?.[tagsProperty]
                ? [...this.selectedTemplate[tagsProperty], selectedTag]
                : [selectedTag]
        });
    }

    toggleAllTagsSelection(tagsProperty: TTagsProperty) {
        const tags =
            tagsProperty === 'tags' ? this.repoTags : this.repoSystemTags;

        const isAllTagsSelected =
            this.selectedTemplate?.[tagsProperty]?.length === tags.length;

        this.editSelectedTemplate({
            [tagsProperty]: isAllTagsSelected ? [] : tags
        });
    }

    // getActiveFilters(filters: IFlowGalleryFilter[]) {
    //     const activeFilters = filters?.filter(({ isActive }) => isActive);
    //     return activeFilters?.length ? activeFilters : null;
    // }

    // @computed
    // get activeTypeFilters() {
    //     return this.getActiveFilters(this.typeFilters);
    // }

    // @computed
    // get activeFilterTags() {
    //     const filterTags: string[] = [];
    //     if (this.singleFilterTag) {
    //         filterTags.push(this.singleFilterTag);
    //     }
    //     if (this.activeTypeFilters) {
    //         filterTags.push(
    //             ...flatten(
    //                 this.activeTypeFilters?.map(({ filter }) => filter.tag)
    //             )
    //         );
    //     }
    //     return [...new Set(filterTags)];
    // }

    // @computed
    // get isNoFilters() {
    //     return (
    //         !this.activeFilterTags.length &&
    //         !this.activeIndustryFilters?.length &&
    //     );
    // }

    // @action.bound
    // setSingleTag(tag: string) {
    //     this.singleFilterTag = tag;
    // }

    // @action.bound
    // clearAllFilters() {
    //     // this.setSingleTag(null);
    //     this.typeFilters.forEach(filter => {
    //         filter.isActive = false;
    //     });
    // }

    // @action.bound
    // loadTypeFilters(filters: IFlowGalleryFilter[] = []) {
    //     this.typeFilters = filters
    //         // TEMP - These filters currently have no templates associated with them in the public repo
    //         .filter(({ key }) => !['briefs', 'educators'].includes(key))
    //         .sort((a, b) => AtoZcomparator(a, b, 'name'));
    // }
}
